'use client'

import type { WelcomeBlock as WelcomeBlockProps } from 'src/payload-types'

import React from 'react'
import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import RichText from '@/components/RichText'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import { Mask } from '@/components/Mask'
import Link from 'next/link'
import { getLink } from '@/utilities/getLink'

type Props = {
  className?: string
} & WelcomeBlockProps

export const WelcomeBlock: React.FC<Props> = ({
  content,
  title,
  hasLink,
  link,
  disableRevealText,
}) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })

  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const paragraphVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
    }),
  }

  return (
    <section className="bg-white py-12 md:py-24">
      <div className="container mx-auto px-6">
        <motion.div ref={ref} className="flex justify-center">
          <Mask width={50} height={50} color="black" />
        </motion.div>
        <motion.div ref={ref} className="mx-auto max-w-3xl">
          <AnimatedText
            text={title}
            type="h2"
            variant="simple"
            className="text-lg! mb-8 text-center font-heading font-medium tracking-wide text-gray-900 md:text-4xl"
            classNameText="text-2xl md:text-4xl"
            delay={0.2}
          />

          <motion.div
            custom={1}
            variants={paragraphVariants}
            initial="hidden"
            animate={disableRevealText ? 'visible' : inView ? 'visible' : 'hidden'}
          >
            <RichText data={content} enableGutter={false} enableProse={false} />
          </motion.div>
        </motion.div>
        {hasLink && (
          <div className="mt-10 flex justify-center">
            <Link href={getLink(link)} target={link?.newTab ? '_blank' : '_self'}>
              <button className="group relative inline-flex items-center overflow-hidden border-2 border-antonie-green px-8 py-3 text-base font-light text-black transition-all duration-300 hover:bg-antonie-green hover:text-black">
                <span className="relative z-10">{link?.label}</span>
                <div className="absolute inset-0 -z-10 h-full w-full scale-x-0 transform bg-antonie-green transition-transform duration-300 group-hover:scale-x-100" />
              </button>
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}
